import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { BLOCK_LABEL } from './emailDoc';
import type { EmailBlock, TemplateSlot } from './types';

interface Props {
  block: EmailBlock;
  index: number;
  count: number;
  slots: TemplateSlot[];
  onChange: (block: EmailBlock) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}

/**
 * One block of an email. The fields differ by type, the frame does not: a title with the block's
 * kind, move up/down, remove. A built-in block (the table of dates in a reschedule notice) can be
 * moved but not edited, and cannot be removed when the template needs it.
 */
export function BlockEditor({ block, index, count, slots, onChange, onMove, onRemove }: Props) {
  const slot = block.type === 'slot' ? slots.find((s) => s.name === block.name) : undefined;
  const locked = block.type === 'slot' && slot?.required;

  return (
    <div className="et-block" data-testid={`et-block-${index}`}>
      <div className="et-block-head">
        <span className="et-block-kind">{BLOCK_LABEL[block.type]}</span>
        <span className="et-block-tools">
          <button type="button" className="et-icon-btn" onClick={() => onMove(index - 1)} disabled={index === 0} aria-label="Move block up">
            <ArrowUp size={13} aria-hidden />
          </button>
          <button type="button" className="et-icon-btn" onClick={() => onMove(index + 1)} disabled={index === count - 1} aria-label="Move block down">
            <ArrowDown size={13} aria-hidden />
          </button>
          <button
            type="button"
            className="et-icon-btn danger"
            onClick={onRemove}
            disabled={locked}
            title={locked ? 'This block is required by the template' : undefined}
            aria-label="Remove block"
          >
            <Trash2 size={13} aria-hidden />
          </button>
        </span>
      </div>
      <div className="et-block-body">{renderFields(block, slot, onChange)}</div>
    </div>
  );
}

function renderFields(
  block: EmailBlock,
  slot: TemplateSlot | undefined,
  onChange: (block: EmailBlock) => void,
) {
  switch (block.type) {
    case 'text':
    case 'note':
      return (
        <textarea
          className="et-input"
          rows={block.type === 'text' ? 3 : 2}
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          aria-label={BLOCK_LABEL[block.type]}
        />
      );

    case 'callout':
      return (
        <>
          <div className="et-row">
            <label className="et-field narrow">
              Tone
              <select
                className="et-input"
                value={block.tone}
                onChange={(e) => onChange({ ...block, tone: e.target.value as typeof block.tone })}
              >
                <option value="info">Info (grey)</option>
                <option value="warn">Warning (amber)</option>
                <option value="bad">Urgent (red)</option>
              </select>
            </label>
          </div>
          <textarea
            className="et-input"
            rows={2}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            aria-label="Callout text"
          />
        </>
      );

    case 'button':
      return (
        <div className="et-row">
          <label className="et-field">
            Label
            <input className="et-input" value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })} />
          </label>
          <label className="et-field">
            Link ({'{{variable}}'} or https://…)
            <input className="et-input" value={block.url} onChange={(e) => onChange({ ...block, url: e.target.value })} spellCheck={false} />
          </label>
        </div>
      );

    case 'list':
      return (
        <>
          <label className="et-field narrow">
            Style
            <select
              className="et-input"
              value={block.style ?? 'bullets'}
              onChange={(e) => onChange({ ...block, style: e.target.value as 'bullets' | 'checks' })}
            >
              <option value="bullets">Bullets</option>
              <option value="checks">Check boxes</option>
            </select>
          </label>
          {block.items.map((item, i) => (
            <div className="et-line" key={i}>
              <input
                className="et-input"
                value={item}
                aria-label={`Item ${i + 1}`}
                onChange={(e) => onChange({ ...block, items: block.items.map((v, j) => (j === i ? e.target.value : v)) })}
              />
              <button
                type="button"
                className="et-icon-btn"
                aria-label={`Remove item ${i + 1}`}
                disabled={block.items.length === 1}
                onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
              >
                <X size={13} aria-hidden />
              </button>
            </div>
          ))}
          <button type="button" className="et-add" onClick={() => onChange({ ...block, items: [...block.items, ''] })}>
            <Plus size={12} aria-hidden /> Add item
          </button>
        </>
      );

    case 'facts':
      return (
        <>
          {block.items.map((row, i) => (
            <div className="et-line" key={i}>
              <input
                className="et-input"
                placeholder="Label"
                value={row.label}
                aria-label={`Row ${i + 1} label`}
                onChange={(e) => onChange({ ...block, items: block.items.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)) })}
              />
              <input
                className="et-input"
                placeholder="Value"
                value={row.value}
                aria-label={`Row ${i + 1} value`}
                onChange={(e) => onChange({ ...block, items: block.items.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)) })}
              />
              <button
                type="button"
                className="et-icon-btn"
                aria-label={`Remove row ${i + 1}`}
                disabled={block.items.length === 1}
                onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
              >
                <X size={13} aria-hidden />
              </button>
            </div>
          ))}
          <button type="button" className="et-add" onClick={() => onChange({ ...block, items: [...block.items, { label: '', value: '' }] })}>
            <Plus size={12} aria-hidden /> Add row
          </button>
        </>
      );

    case 'tiles':
      return (
        <>
          {block.items.map((tile, i) => (
            <div className="et-line" key={i}>
              <input
                className="et-input"
                placeholder="Big number"
                value={tile.value}
                aria-label={`Tile ${i + 1} number`}
                onChange={(e) => onChange({ ...block, items: block.items.map((t, j) => (j === i ? { ...t, value: e.target.value } : t)) })}
              />
              <input
                className="et-input"
                placeholder="Label"
                value={tile.label}
                aria-label={`Tile ${i + 1} label`}
                onChange={(e) => onChange({ ...block, items: block.items.map((t, j) => (j === i ? { ...t, label: e.target.value } : t)) })}
              />
              <label className="et-check" title="Paint this tile in ink with a cyan figure">
                <input
                  type="checkbox"
                  checked={!!tile.accent}
                  onChange={(e) => onChange({ ...block, items: block.items.map((t, j) => (j === i ? { ...t, accent: e.target.checked } : { ...t, accent: false })) })}
                />
                Accent
              </label>
              <button
                type="button"
                className="et-icon-btn"
                aria-label={`Remove tile ${i + 1}`}
                disabled={block.items.length === 1}
                onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
              >
                <X size={13} aria-hidden />
              </button>
            </div>
          ))}
          {block.items.length < 3 && (
            <button type="button" className="et-add" onClick={() => onChange({ ...block, items: [...block.items, { value: '', label: '' }] })}>
              <Plus size={12} aria-hidden /> Add tile
            </button>
          )}
        </>
      );

    case 'slot':
      return (
        <p className="et-slot">
          <strong>{block.name}</strong> — {slot?.description ?? 'Filled in by the platform.'}
          {slot?.required ? ' Required: it is the record of what happened.' : ''}
        </p>
      );
  }
}
